import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, ShieldAlert, Sparkles, DollarSign, RefreshCcw, Eye, EyeOff,
  CheckCircle2, XCircle, AlertTriangle, ExternalLink, Loader2, Trash2, Mail, Send,
} from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const PROVIDER_META = {
  openai:   { label: "OpenAI",              color: "#10b981", accent: "OpenAI Direct SDK" },
  emergent: { label: "Emergent",            color: "#3b82f6", accent: "Emergent LLM Key" },
  none:     { label: "None (fallbacks)",    color: "#ef4444", accent: "No key configured" },
};

const usdFmt = (n) => `$${(Number(n) || 0).toFixed(4)}`;

function StatusPill({ status }) {
  const map = {
    ok:      { icon: CheckCircle2, color: "#10b981", label: "Active" },
    error:   { icon: XCircle,      color: "#ef4444", label: "Invalid" },
    untested:{ icon: AlertTriangle,color: "#f59e0b", label: "Untested" },
    none:    { icon: XCircle,      color: "#6b7280", label: "None" },
  };
  const m = map[status] || map.untested;
  const Icon = m.icon;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest"
      style={{ color: m.color, borderColor: `${m.color}55`, background: `${m.color}15` }}
      data-testid={`status-pill-${status}`}
    >
      <Icon className="w-3 h-3" /> {m.label}
    </span>
  );
}

export default function Settings() {
  const [providerInfo, setProviderInfo] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loadingUsage, setLoadingUsage] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [saving, setSaving] = useState(false);
  const [clearing, setClearing] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);   // {status, latency_ms, error?}

  const refresh = async () => {
    try {
      const r = await api.get("/admin/llm-provider");
      setProviderInfo(r.data);
    } catch (e) {
      toast.error("Failed to load provider info");
    }
  };

  const refreshUsage = async () => {
    setLoadingUsage(true);
    try {
      const r = await api.get("/ai/usage?days=30");
      setUsage(r.data);
    } catch (e) {
      // silent — usage endpoint may return empty
    } finally {
      setLoadingUsage(false);
    }
  };

  useEffect(() => {
    refresh();
    refreshUsage();
  }, []);

  const handleVerifyExisting = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const r = await api.post("/admin/verify-llm");
      setVerifyResult(r.data);
      if (r.data.status === "ok") {
        toast.success(`LLM verified — ${r.data.latency_ms} ms`);
      } else {
        toast.error(r.data.error || "LLM verification failed");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Verification error");
    } finally {
      setVerifying(false);
      refreshUsage();
    }
  };

  const handleSave = async () => {
    if (!newKey.startsWith("sk-") || newKey.length < 20) {
      toast.error("Key must start with 'sk-' and be at least 20 characters");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/set-llm-key", {
        provider: "openai",
        api_key: newKey,
        verify_first: true,
      });
      if (r.data.status === "ok") {
        toast.success(r.data.message || "Key saved and activated");
        setNewKey("");
        setShowKey(false);
        setVerifyResult({ status: "ok", latency_ms: 0 });
        refresh();
      } else {
        toast.error(r.data.error || "Key was NOT saved — verification failed");
        setVerifyResult({ status: "error", latency_ms: r.data.latency_ms, error: r.data.error });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleClearDbKey = async () => {
    if (!window.confirm("Remove the DB-stored key? The system will fall back to the .env value (if any) after next backend restart.")) return;
    setClearing(true);
    try {
      await api.delete("/admin/set-llm-key");
      toast.success("DB-stored key cleared");
      refresh();
    } catch (e) {
      toast.error("Failed to clear key");
    } finally {
      setClearing(false);
    }
  };

  const meta = providerInfo ? (PROVIDER_META[providerInfo.provider] || PROVIDER_META.none) : PROVIDER_META.none;
  const overallStatus = !providerInfo ? "untested"
    : providerInfo.provider === "none" ? "none"
    : verifyResult?.status || "untested";

  return (
    <div className="p-6 md:p-8 max-w-5xl mx-auto space-y-6" data-testid="settings-page">
      <header>
        <h1 className="font-heading font-black text-3xl tracking-tighter">Settings</h1>
        <div className="vx-label mt-1">API keys · Usage · Security</div>
      </header>

      {/* ==================== CARD 1 — LLM PROVIDER ==================== */}
      <section
        className="rounded-lg border border-[#1f2937] bg-[#111827]"
        data-testid="settings-llm-card"
      >
        <div className="px-5 py-4 border-b border-[#1f2937] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-[#3b82f6]" />
            <div className="font-semibold text-sm">LLM Provider</div>
          </div>
          <StatusPill status={overallStatus} />
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <div className="vx-label mb-1">Provider</div>
              <div className="font-mono text-sm" style={{ color: meta.color }} data-testid="provider-name">
                {meta.label}
              </div>
              <div className="text-[10px] text-[#6b7280] font-mono mt-0.5">{meta.accent}</div>
            </div>
            <div>
              <div className="vx-label mb-1">Current key</div>
              <div className="font-mono text-sm text-[#e5e7eb]" data-testid="current-key-masked">
                {providerInfo?.openai_key_masked || (providerInfo?.emergent_key_present ? "(emergent internal)" : "—")}
              </div>
              <div className="text-[10px] text-[#6b7280] font-mono mt-0.5">
                Source: {providerInfo?.openai_key_source || (providerInfo?.emergent_key_present ? "env" : "—")}
              </div>
            </div>
            <div className="flex flex-col justify-between">
              <div>
                <div className="vx-label mb-1">Last verify</div>
                <div className="font-mono text-sm">
                  {verifyResult
                    ? verifyResult.status === "ok"
                      ? <span className="text-[#10b981]">✓ {verifyResult.latency_ms} ms</span>
                      : <span className="text-[#ef4444]">✗ {verifyResult.error?.slice(0, 40) || "error"}</span>
                    : <span className="text-[#6b7280]">—</span>}
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={handleVerifyExisting}
                disabled={verifying || providerInfo?.provider === "none"}
                className="mt-2 border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono h-8"
                data-testid="verify-existing-btn"
              >
                {verifying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
                Test current key
              </Button>
            </div>
          </div>

          <div className="border-t border-[#1f2937] pt-4">
            <div className="vx-label mb-2 flex items-center gap-2">
              <Sparkles className="w-3 h-3 text-[#a78bfa]" /> Rotate / add key
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1 relative">
                <Input
                  type={showKey ? "text" : "password"}
                  autoComplete="off"
                  placeholder="sk-proj-…"
                  value={newKey}
                  onChange={(e) => setNewKey(e.target.value)}
                  className="pr-10 font-mono text-xs bg-[#0a0e1a] border-[#1f2937]"
                  data-testid="new-key-input"
                />
                <button
                  type="button"
                  onClick={() => setShowKey((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#e5e7eb]"
                  aria-label={showKey ? "Hide key" : "Show key"}
                  data-testid="toggle-key-visibility"
                >
                  {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Button
                onClick={handleSave}
                disabled={saving || !newKey}
                className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold text-xs"
                data-testid="save-key-btn"
              >
                {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
                Verify & Save
              </Button>
            </div>
            <div className="text-[10px] text-[#6b7280] mt-2 font-mono leading-relaxed">
              The key is <span className="text-[#f59e0b]">verified with a live test call BEFORE persisting</span> — a
              broken key will never replace a working one. Stored encrypted in the database.
            </div>
          </div>

          {providerInfo?.openai_key_source === "database" && (
            <div className="border-t border-[#1f2937] pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={handleClearDbKey}
                disabled={clearing}
                className="border-[#ef4444]/40 text-[#ef4444] hover:bg-[#ef4444]/10 text-xs font-mono h-8"
                data-testid="clear-db-key-btn"
              >
                {clearing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Trash2 className="w-3 h-3 mr-1" />}
                Remove DB-stored key
              </Button>
              <div className="text-[10px] text-[#6b7280] mt-1 font-mono">
                Reverts to the .env value (or emergent fallback) after next backend restart.
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ==================== CARD 2 — USAGE & COSTS ==================== */}
      <section
        className="rounded-lg border border-[#1f2937] bg-[#111827]"
        data-testid="settings-usage-card"
      >
        <div className="px-5 py-4 border-b border-[#1f2937] flex items-center justify-between">
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-[#10b981]" />
            <div className="font-semibold text-sm">Usage & Cost (last 30 days)</div>
          </div>
          <Button
            variant="outline" size="sm" onClick={refreshUsage} disabled={loadingUsage}
            className="border-[#1f2937] hover:bg-[#1f2937] text-xs font-mono h-7"
            data-testid="refresh-usage-btn"
          >
            {loadingUsage ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCcw className="w-3 h-3 mr-1" />}
            Refresh
          </Button>
        </div>
        <div className="p-5">
          {!usage ? (
            <div className="text-[11px] text-[#6b7280] font-mono">Loading…</div>
          ) : usage.total_calls === 0 ? (
            <div className="text-[11px] text-[#6b7280] font-mono">
              No LLM calls in the last {usage.days} days. Trigger any AI feature to start tracking.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <div className="rounded border border-[#1f2937] p-3">
                  <div className="vx-label">Calls</div>
                  <div className="text-2xl font-mono font-black text-[#e5e7eb]" data-testid="usage-total-calls">
                    {usage.total_calls}
                  </div>
                </div>
                <div className="rounded border border-[#1f2937] p-3">
                  <div className="vx-label">Tokens</div>
                  <div className="text-2xl font-mono font-black text-[#3b82f6]" data-testid="usage-total-tokens">
                    {usage.total_tokens.toLocaleString()}
                  </div>
                </div>
                <div className="rounded border border-[#1f2937] p-3">
                  <div className="vx-label">Est. cost</div>
                  <div className="text-2xl font-mono font-black text-[#10b981]" data-testid="usage-total-cost">
                    {usdFmt(usage.total_cost_estimate_usd)}
                  </div>
                </div>
              </div>
              <div>
                <div className="vx-label mb-2">By feature</div>
                <table className="w-full text-xs font-mono">
                  <thead>
                    <tr className="text-[#6b7280] border-b border-[#1f2937]">
                      <th className="py-2 px-2 text-left">Feature</th>
                      <th className="py-2 px-2 text-right">Calls</th>
                      <th className="py-2 px-2 text-right">Tokens</th>
                      <th className="py-2 px-2 text-right">Cost</th>
                    </tr>
                  </thead>
                  <tbody data-testid="usage-by-feature">
                    {usage.by_feature.map((row) => (
                      <tr key={row.feature} className="border-b border-[#1f2937]" data-testid={`usage-row-${row.feature}`}>
                        <td className="py-2 px-2 text-[#e5e7eb]">{row.feature}</td>
                        <td className="py-2 px-2 text-right text-[#9ca3af]">{row.calls}</td>
                        <td className="py-2 px-2 text-right text-[#3b82f6]">{row.total_tokens.toLocaleString()}</td>
                        <td className="py-2 px-2 text-right text-[#10b981]">{usdFmt(row.cost_estimate_usd)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* ==================== CARD — SENDGRID EMAIL (admin-only) ==================== */}
      <SendGridCard />

      {/* ==================== CARD 3 — SECURITY GUIDELINES ==================== */}
      <section
        className="rounded-lg border border-[#f59e0b]/30 bg-gradient-to-br from-[#f59e0b]/5 to-transparent"
        data-testid="settings-security-card"
      >
        <div className="px-5 py-4 border-b border-[#f59e0b]/30 flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#f59e0b]" />
          <div className="font-semibold text-sm text-[#f59e0b]">Security guidelines</div>
        </div>
        <div className="p-5 text-xs text-[#e5e7eb] space-y-2 leading-relaxed font-mono">
          <div>· <span className="text-[#f59e0b]">Never share API keys via chat, email, screenshots, or public forums.</span> If a key was pasted anywhere non-private, rotate it immediately.</div>
          <div>· Keys pasted into this page are validated with a live test call BEFORE persisting — a broken key will never replace a working one.</div>
          <div>· Stored keys are encrypted at rest using Fernet symmetric encryption (from <code className="text-[#3b82f6]">ALERT_ENCRYPTION_KEY</code>).</div>
          <div>· Logs are automatically scrubbed for any <code className="text-[#3b82f6]">sk-*</code> pattern — even accidental leaks by 3rd-party libraries.</div>
          <div>· If a key is compromised, rotate on the provider dashboard first, then paste the new key here.</div>
          <div className="pt-2">
            <a
              href="https://platform.openai.com/api-keys"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#3b82f6] hover:text-[#60a5fa]"
              data-testid="openai-dashboard-link"
            >
              OpenAI API keys dashboard <ExternalLink className="w-3 h-3" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

// ==================== SendGrid card ====================
function SendGridCard() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const [fromName, setFromName] = useState("");
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/admin/sendgrid-config");
      setCfg(r.data);
      setFromEmail(r.data.from_email || "");
      setFromName(r.data.from_name || "");
    } catch (e) {
      // 403 → not admin — hide card
      setCfg({ hidden: true });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (loading) return null;
  if (cfg?.hidden) return null;   // non-admin doesn't see this card

  const save = async () => {
    if (!apiKey.startsWith("SG.") || apiKey.length < 20) {
      toast.error("SendGrid key must start with 'SG.' (from https://app.sendgrid.com/settings/api_keys)");
      return;
    }
    setSaving(true);
    try {
      const r = await api.post("/admin/set-sendgrid-key", {
        api_key: apiKey,
        from_email: fromEmail,
        from_name: fromName,
        verify_first: true,
      });
      if (r.data.status === "ok") {
        toast.success(r.data.message || "Saved");
        setApiKey("");
        setShowKey(false);
        load();
      } else {
        toast.error(r.data.error || "Save failed");
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/sendgrid-test", {});
      toast[r.data.status === "ok" ? "success" : "error"](
        r.data.status === "ok" ? `Test email sent to ${r.data.to}` : "Send failed",
      );
    } catch (e) { toast.error("Test failed"); }
    finally { setTesting(false); }
  };

  return (
    <section
      className="rounded-lg border border-[#1f2937] bg-[#111827]"
      data-testid="settings-sendgrid-card"
    >
      <div className="px-5 py-4 border-b border-[#1f2937] flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Mail className="w-4 h-4 text-[#3b82f6]" />
          <div className="font-semibold text-sm">Email Service (SendGrid)</div>
        </div>
        <span
          className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-widest"
          style={{
            color:       cfg.configured ? "#10b981" : "#f59e0b",
            borderColor: cfg.configured ? "#10b98155" : "#f59e0b55",
            background:  cfg.configured ? "#10b98115" : "#f59e0b15",
          }}
          data-testid="sendgrid-status"
        >
          {cfg.configured ? "✓ Configured" : "⚠ Not configured"}
        </span>
      </div>

      <div className="p-5 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <div className="vx-label mb-1">Current key</div>
            <div className="font-mono text-sm text-[#e5e7eb]" data-testid="sendgrid-current-key">
              {cfg.masked_key || "—"}
            </div>
            <div className="text-[10px] text-[#6b7280] font-mono">source: {cfg.source || "—"}</div>
          </div>
          <div>
            <div className="vx-label mb-1">From email</div>
            <div className="font-mono text-xs text-[#e5e7eb]">{cfg.from_email}</div>
          </div>
          <div>
            <div className="vx-label mb-1">From name</div>
            <div className="font-mono text-xs text-[#e5e7eb]">{cfg.from_name}</div>
          </div>
        </div>

        <div className="border-t border-[#1f2937] pt-4 space-y-3">
          <div className="vx-label">Add / rotate key</div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <Input
                type={showKey ? "text" : "password"}
                autoComplete="off"
                placeholder="SG.xxx…"
                value={apiKey}
                onChange={(e) => setApiKey(e.target.value)}
                className="pr-10 font-mono text-xs bg-[#0a0e1a] border-[#1f2937]"
                data-testid="sendgrid-key-input"
              />
              <button
                type="button"
                onClick={() => setShowKey((s) => !s)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-[#6b7280] hover:text-[#e5e7eb]"
                aria-label={showKey ? "Hide" : "Show"}
              >
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <Input value={fromEmail} onChange={(e) => setFromEmail(e.target.value)}
                   placeholder="noreply@veerox.ai"
                   className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs" data-testid="sendgrid-from-email" />
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)}
                   placeholder="Veerox INTEL AI"
                   className="bg-[#0a0e1a] border-[#1f2937] font-mono text-xs" data-testid="sendgrid-from-name" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button
              variant="outline" size="sm" onClick={sendTest}
              disabled={testing || !cfg.configured}
              className="border-[#1f2937] hover:bg-[#1f2937] text-xs h-8"
              data-testid="sendgrid-test-btn"
            >
              {testing ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Send className="w-3 h-3 mr-1" />}
              Send test email
            </Button>
            <Button
              onClick={save} disabled={saving || !apiKey}
              className="bg-[#10b981] hover:bg-[#059669] text-black font-semibold text-xs"
              data-testid="sendgrid-save-btn"
            >
              {saving ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
              Verify & Save
            </Button>
          </div>
          <div className="text-[10px] text-[#6b7280] font-mono leading-relaxed">
            Get a key from{" "}
            <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer"
               className="text-[#3b82f6] hover:underline">
              sendgrid.com → Settings → API Keys
            </a>
            . Verification sends a test email to your admin address — a broken key will not be persisted.
          </div>
        </div>
      </div>
    </section>
  );
}
